// components/BookingRequest/FileUploadPreview.tsx
import React from "react";
import { FilmIcon, TableCellsIcon } from "@heroicons/react/24/outline";
import { FiBarChart2, FiFileText } from "react-icons/fi";
import { BiArchive } from "react-icons/bi";

interface FilePreview {
  type: string;
  content?: string;
  name: string;
  extension?: string;
}

interface FileUploadPreviewProps {
  selectedFiles: File[];
  previews: FilePreview[];
  onRemoveFile: (index: number) => void;
}

const FileUploadPreview: React.FC<FileUploadPreviewProps> = ({
  selectedFiles,
  previews,
  onRemoveFile,
}) => {
  const renderFileIcon = (preview: FilePreview, index: number) => {
    if (preview.type === "image" && preview.content) {
      return (
        <div
          className="w-8 h-8 relative overflow-hidden rounded-sm"
          key={index}
        >
          <img
            src={preview.content}
            alt="Preview"
            className="w-full h-full object-cover"
          />
        </div>
      );
    }

    switch (preview.type) {
      case "video":
        return <FilmIcon className="w-4 h-4 text-blue-500" key={index} />;
      case "pdf":
        return <FiFileText className="w-4 h-4 text-red-500" key={index} />;
      case "doc":
        return <FiFileText className="w-4 h-4 text-blue-500" key={index} />;
      case "excel":
        return (
          <TableCellsIcon className="w-4 h-4 text-green-500" key={index} />
        );
      case "powerpoint":
        return <FiBarChart2 className="w-4 h-4 text-orange-500" key={index} />;
      case "text":
        return <FiFileText className="w-4 h-4 text-gray-500" key={index} />;
      case "zip":
        return <BiArchive className="w-4 h-4 text-yellow-500" key={index} />;
      default:
        return (
          <div
            className="w-4 h-4 flex items-center justify-center bg-gray-300 dark:bg-gray-700 rounded"
            key={index}
          >
            <span className="text-[10px]">
              {preview.extension?.toUpperCase().substring(0, 3)}
            </span>
          </div>
        );
    }
  };

  if (selectedFiles.length === 0) return null;

  return (
    <div className="mt-3 flex flex-wrap gap-2">
      {selectedFiles.map((file, index) => (
        <div
          key={index}
          className="flex items-center gap-2 p-2 bg-gray-200 dark:bg-gray-800 rounded-md max-w-fit hover:bg-gray-300 dark:hover:bg-gray-700 transition-colors"
        >
          {previews[index] && renderFileIcon(previews[index], index)}

          <span className="text-sm text-gray-600 dark:text-gray-400 max-w-[100px] truncate">
            {file.name}
          </span>

          <button
            type="button"
            onClick={() => onRemoveFile(index)}
            className="ml-2 p-1 hover:bg-gray-400 dark:hover:bg-gray-600 rounded-full transition-colors"
            aria-label="Remove file"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={1.5}
              stroke="currentColor"
              className="w-4 h-4 text-gray-500 dark:text-gray-400"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>
      ))}
    </div>
  );
};

export default FileUploadPreview;
